<template>
  <div class="login-page">
    <div class="language-switcher">
      <language-switcher />
    </div>

    <div class="login-container">
      <div class="login-header">
        <div class="logo-container">
          <img src="@/assets/logo.png" alt="Equestrian Booking Club" class="logo" />
        </div>
        <h1 class="title">{{ $t('login.title') }}</h1>
        <p class="subtitle">{{ $t('login.subtitle') }}</p>
      </div>
      
      <el-tabs v-model="activeTab" class="login-tabs">
        <el-tab-pane :label="$t('login.password_login')" name="password">
          <el-form
            ref="passwordFormRef"
            :model="passwordForm"
            :rules="passwordRules"
            class="login-form"
            @keyup.enter="handlePasswordLogin"
          >
            <el-form-item prop="username">
              <el-input
                v-model="passwordForm.username"
                :placeholder="$t('login.username_placeholder')"
                size="large"
                clearable
                @input="handleUsernameInput"
              >
                <template #prefix>
                  <el-icon><User /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="password">
              <el-input
                v-model="passwordForm.password"
                type="password"
                :placeholder="$t('login.password_placeholder')"
                size="large"
                show-password
                clearable
              >
                <template #prefix>
                  <el-icon><Lock /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item>
              <el-button
                type="primary"
                size="large"
                class="login-btn"
                :loading="loading"
                @click="handlePasswordLogin"
              >
                {{ $t('login.login_button') }}
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
        
        <el-tab-pane :label="$t('login.code_login')" name="sms">
          <el-form
            ref="smsFormRef"
            :model="smsForm"
            :rules="smsRules"
            class="login-form"
            @keyup.enter="handleSmsLogin"
          >
            <el-form-item prop="phone">
              <el-input
                v-model="smsForm.phone"
                :placeholder="$t('login.phone_required')"
                size="large"
                clearable
                @input="handlePhoneInput"
              >
                <template #prefix>
                  <el-icon><Phone /></el-icon>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item prop="code">
              <el-input
                v-model="smsForm.code"
                :placeholder="$t('login.code_hint')"
                size="large"
                clearable
                @input="handleSmsCodeInput"
              >
                <template #prefix>
                  <el-icon><Message /></el-icon>
                </template>
                <template #append>
                  <el-button 
                    :disabled="smsCounting || smsSending" 
                    :loading="smsSending"
                    @click="sendSmsCode"
                  >
                    {{ smsSending ? '' : (smsCounting ? $t('login.resend_in', { seconds: smsCountdown }) : $t('login.send_code')) }}
                  </el-button>
                </template>
              </el-input>
            </el-form-item>
            
            <el-form-item>
              <el-button
                type="primary"
                size="large"
                class="login-btn"
                :loading="loading"
                @click="handleSmsLogin"
              >
                {{ $t('login.login_button') }}
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
      

    </div>

    <!-- Footer Beian Information -->
    <div class="login-footer">
      <a href="https://beian.mps.gov.cn/#/query/webSearch?code=65010202001341" target="_blank" rel="noopener noreferrer" class="beian-link">
        <img src="@/assets/police.png" alt="Beian"/>
        <span>新公网安备65010202001341号</span>
      </a>
      
      <span class="separator">|</span>
     <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" class="beian-link">
        <span>新ICP备2025023793号-1</span>
      </a>
    </div>

    <!-- Customer Service FAB -->
    <CustomerServiceFAB />
  </div>
</template>

<script setup>
import { ref, reactive, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { login as apiLogin, sendSms as apiSendSms } from '@/api/auth'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import CustomerServiceFAB from '@/components/CustomerServiceFAB.vue'
import { User, Lock, Phone, Message, Files, CircleCheck } from '@element-plus/icons-vue'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()

const activeTab = ref('password')
const passwordFormRef = ref(null)
const smsFormRef = ref(null)
const loading = ref(false)
const smsSending = ref(false)
const smsCounting = ref(false)
const smsCountdown = ref(0)
let smsTimer = null

onUnmounted(() => {
  if (smsTimer) {
    clearInterval(smsTimer)
  }
})

const passwordForm = reactive({
  username: '',
  password: ''
})

const smsForm = reactive({
  phone: '',
  code: ''
})

const handlePhoneInput = (value) => {
  smsForm.phone = value.replace(/\D/g, '').slice(0, 11)
}

const handleSmsCodeInput = (value) => {
  smsForm.code = value.replace(/\D/g, '').slice(0, 6)
}

const handleUsernameInput = (value) => {
  // Only allow digits and limit to 11 characters for phone number input
  passwordForm.username = value.replace(/\D/g, '').slice(0, 11)
}

const passwordRules = {
  username: [
    { required: true, message: t('validation.required'), trigger: 'blur' }
  ],
  password: [
    { required: true, message: t('validation.required'), trigger: 'blur' },
    { min: 5, max: 16, message: t('validation.minLength', { min: 5 }), trigger: 'blur' }
  ]
}

const smsRules = {
  phone: [
    { required: true, message: t('validation.required'), trigger: ['blur', 'change'] },
    { min: 11, max: 11, message: t('login.phone_length_invalid'), trigger: ['blur', 'change'] },
    { 
      validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error(t('validation.required')))
        } else if (!/^\d+$/.test(value)) {
          callback(new Error(t('login.phone_format_invalid')))
        } else if (value.length !== 11) {
          callback(new Error(t('login.phone_length_invalid')))
        } else {
          callback()
        }
      }, 
      trigger: ['blur', 'change'] 
    }
  ],
  code: [
    { required: true, message: t('validation.required'), trigger: 'blur' },
    { min: 4, max: 6, message: t('login.code_length_invalid'), trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error(t('validation.required')))
        } else if (!/^\d+$/.test(value)) {
          callback(new Error(t('login.code_format_invalid')))
        } else if (value.length < 4 || value.length > 6) {
          callback(new Error(t('login.code_length_invalid')))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

const handlePasswordLogin = async () => {
  if (!passwordFormRef.value) return
  
  await passwordFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      const startTime = performance.now()
      console.log(`[Login] ${new Date().toLocaleTimeString()} - Starting login process`)
      
      try {
        console.log(`[Login] ${new Date().toLocaleTimeString()} - Sending login request...`)
        const res = await apiLogin({
          phone: passwordForm.username,
          password: passwordForm.password
        })
        console.log(`[Login] ${new Date().toLocaleTimeString()} - Login response received (${((performance.now() - startTime) / 1000).toFixed(2)}s)`)
        
        if (res.success) {
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Processing user data...`)
          const userData = res.data
          const token = userData.token

          // Check if account is inactive (status != 1) or no token
          if (!token || userData.status !== 1) {
            const statusMessages = {
              0: t('login.account_inactive'),
              2: t('login.account_deleted'),
              3: t('login.account_blocked')
            }
            const statusMsg = statusMessages[userData.status] || t('login.account_inactive')
            ElMessage.warning(statusMsg)
            return
          }

          const userInfo = {
            id: userData.id,
            name: userData.name,
            phone: userData.phone,
            role: userData.role || 'user',
            teachingFee: userData.teachingFee,
            status: userData.status,
            level: userData.level,
            ruleKind: userData.ruleKind,
            age: userData.age,
            workExperienceYear: userData.workExperienceYear,
            acquiredMedals: userData.acquiredMedals,
            remark: userData.remark,
            avatar: userData.avatar,
            openId: userData.openId
          }
          
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Setting auth state...`)
          const permissions = userData.role === 'Admin' ? ['admin', '*'] : ['user']
          userStore.setAuth(token, userInfo, permissions)
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Auth state set (${((performance.now() - startTime) / 1000).toFixed(2)}s)`)
          
          // Fetch complete profile data
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Fetching user profile...`)
          await userStore.fetchUserProfile()
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Profile fetch completed`)
          
          ElMessage.success(t('login.login_success'))
          
          // Navigate immediately without waiting for Promise
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Navigating to dashboard...`)
          router.push('/dashboard').then(() => {
            console.log(`[Login] ${new Date().toLocaleTimeString()} - Navigation confirmed`)
          }).catch(err => {
            console.error(`[Login] Navigation error:`, err)
          })
          console.log(`[Login] ${new Date().toLocaleTimeString()} - Navigation initiated (${((performance.now() - startTime) / 1000).toFixed(2)}s)`)
        }
      } catch (error) {
        console.error('Login failed:', error)
        ElMessage.error(error.message || 'Login failed')
      } finally {
        loading.value = false
      }
    }
  })
}

const sendSmsCode = async () => {
  if (!smsFormRef.value) return
  
  await smsFormRef.value.validateField('phone', async (valid) => {
    if (valid && !smsCounting.value && !smsSending.value) {
      smsSending.value = true
      try {
        await apiSendSms(smsForm.phone)
        ElMessage.success(t('login.code_send_success'))
        smsCounting.value = true
        smsCountdown.value = 60
        smsTimer = setInterval(() => {
          smsCountdown.value--
          if (smsCountdown.value <= 0) {
            clearInterval(smsTimer)
            smsTimer = null
            smsCounting.value = false
          }
        }, 1000)
      } catch (error) {
        console.error('Send SMS failed:', error)
        ElMessage.error(error.message || t('login.code_send_failed'))
      } finally {
        smsSending.value = false
      }
    }
  })
}

const handleSmsLogin = async () => {
  if (!smsFormRef.value) return
  
  await smsFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      const startTime = performance.now()
      console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Starting SMS login process`)
      
      try {
        console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Sending login request...`)
        const res = await apiLogin({
          phone: smsForm.phone,
          verificationCode: smsForm.code
        })
        console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Login response received (${((performance.now() - startTime) / 1000).toFixed(2)}s)`)
        
        if (res.success) {
          console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Processing user data...`)
          const userData = res.data
          const token = userData.token

          // Check if account is inactive (status != 1) or no token
          if (!token || userData.status !== 1) {
            const statusMessages = {
              0: t('login.account_inactive'),
              2: t('login.account_deleted'),
              3: t('login.account_blocked')
            }
            const statusMsg = statusMessages[userData.status] || t('login.account_inactive')
            ElMessage.warning(statusMsg)
            return
          }

          const userInfo = {
            id: userData.id,
            name: userData.name,
            phone: userData.phone,
            role: userData.role || 'user',
            teachingFee: userData.teachingFee,
            status: userData.status,
            level: userData.level,
            ruleKind: userData.ruleKind,
            age: userData.age,
            workExperienceYear: userData.workExperienceYear,
            acquiredMedals: userData.acquiredMedals,
            remark: userData.remark,
            avatar: userData.avatar,
            openId: userData.openId
          }
          
          console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Setting auth state...`)
          const permissions = userData.role === 'Admin' ? ['admin', '*'] : ['user']
          userStore.setAuth(token, userInfo, permissions)
          console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Auth state set (${((performance.now() - startTime) / 1000).toFixed(2)}s)`)
          
          // Fetch complete profile data
          console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Fetching user profile...`)
          await userStore.fetchUserProfile()
          console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Profile fetch completed`)
          
          ElMessage.success(t('login.login_success'))
          
          console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Navigating to dashboard...`)
          router.push('/dashboard').then(() => {
            console.log(`[SMS Login] ${new Date().toLocaleTimeString()} - Navigation confirmed`)
          }).catch(err => {
            console.error(`[SMS Login] Navigation error:`, err)
          })
        }
      } catch (error) {
        console.error('SMS login failed:', error)
        ElMessage.error(error.message || 'SMS login failed')
      } finally {
        loading.value = false
      }
    }
  })
}
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  animation: gradientBackground 15s ease infinite;
  background-size: 200% 200%;
  padding: 20px;
  
  @media (max-width: 576px) {
    padding: 16px;
    align-items: flex-start;
    padding-top: 40px;
  }
}

@keyframes gradientBackground {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.login-container {
  width: 100%;
  max-width: 400px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  @media (max-width: 576px) {
    padding: 24px 20px;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.35);
    
    @media (max-width: 768px) {
      transform: none;
    }
  }
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
  
  @media (max-width: 576px) {
    margin-bottom: 24px;
  }
  
  .logo-container {
    margin-bottom: 20px;
    
    @media (max-width: 576px) {
      margin-bottom: 16px;
    }
    
    .logo {
      width: 120px;
      height: auto;
      object-fit: contain;
      
      @media (max-width: 576px) {
        width: 100px;
      }
    }
  }
  
  .title {
    font-size: 24px;
    color: #333;
    margin-bottom: 8px;
    font-weight: 600;
    
    @media (max-width: 576px) {
      font-size: 20px;
      margin-bottom: 6px;
    }
  }
  
  .subtitle {
    color: #666;
    font-size: 14px;
    
    @media (max-width: 576px) {
      font-size: 13px;
    }
  }
}

.login-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 20px;
    
    @media (max-width: 576px) {
      margin-bottom: 16px;
    }
  }
  
  :deep(.el-tabs__item) {
    font-size: 16px;
    padding: 0 16px;
    
    @media (max-width: 576px) {
      font-size: 14px;
      padding: 0 12px;
    }
    
    &.is-active {
      font-weight: 600;
    }
  }
  
  :deep(.el-tabs__nav-wrap::after) {
    height: 1px;
  }
  
  :deep(.el-tabs__active-bar) {
    height: 3px;
    border-radius: 2px;
  }
}

.login-form {
  .login-btn {
    width: 100%;
    margin-top: 10px;
    height: 48px;
    font-size: 16px;
    
    @media (max-width: 576px) {
      height: 44px;
      font-size: 15px;
      margin-top: 8px;
    }
  }
}

.language-switcher {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 100;
  
  @media (max-width: 576px) {
    top: 12px;
    right: 12px;
  }
}

.language-switcher .el-button {
  transition: all 0.3s ease;

  &:hover {
    color: #667eea;
  }
}

.login-footer {
  position: fixed;
  bottom: 12px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 8px 16px;
  font-size: 8px;
  color: rgba(255, 255, 255, 0.65);
  z-index: 10;
  
  @media (max-width: 576px) {
    bottom: 6px;
    font-size: 7px;
    gap: 4px;
    padding: 4px 8px;
  }
  
  .separator {
    color: rgba(255, 255, 255, 0.35);
  }
  
  .beian-link {
    display: flex;
    align-items: center;
    gap: 3px;
    color: rgba(255, 255, 255, 0.65);
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: inherit;
    white-space: nowrap;
    
    &:hover {
      color: #fff;
      text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
    }
    
    img {
      width: 12px;
      height: 12px;
      object-fit: contain;
      flex-shrink: 0;
    }
    
    .el-icon {
      flex-shrink: 0;
    }
  }
  
  @media (max-width: 576px) {
    flex-wrap: wrap;
    
    .beian-link {
      font-size: 7px;
      
      img {
        width: 10px;
        height: 10px;
      }
    }
  }
}

.el-input {
  transition: all 0.3s ease;

  &:focus-within {
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.3);
  }
  
  :deep(.el-input__wrapper) {
    height: 50px;
    
    @media (max-width: 576px) {
      height: 46px;
    }
  }
  
  :deep(.el-input__inner) {
    height: 50px;
    font-size: 16px;
    padding: 0 15px;
    
    @media (max-width: 576px) {
      height: 46px;
      font-size: 15px;
      padding: 0 12px;
    }
  }
  
  :deep(.el-input__prefix) {
    padding-left: 12px;
    
    @media (max-width: 576px) {
      padding-left: 10px;
    }
  }
}

.el-button {
  transition: all 0.3s ease;
}

.el-button--loading {
  .el-icon-loading {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.el-link {
  transition: color 0.3s ease;

  &:hover {
    color: #667eea;
  }
}

@media (max-width: 576px) {
  :deep(.el-form-item) {
    margin-bottom: 18px;
  }
  
  :deep(.el-form-item__error) {
    font-size: 12px;
    padding-top: 4px;
  }
  
  :deep(.el-input-group__append) {
    padding: 0 12px;
    
    .el-button {
      padding: 0 8px;
      font-size: 13px;
    }
  }
  
  :deep(.el-tabs__content) {
    overflow: visible;
  }
}
</style>
